import { motion } from "framer-motion";
import { Check, Database, Workflow, TrendingUp, CalendarCheck } from "lucide-react";
import { Reveal, SectionHead } from "../components/bits";

const ease = [0.22, 1, 0.36, 1];

const V = {
    bookings: (
        <div className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: 21 }).map((_, i) => (
                <motion.span
                    key={i}
                    initial={{ opacity: 0.25 }}
                    whileInView={i === 4 || i === 10 || i === 16 ? { opacity: 1, scale: [1, 1.15, 1] } : {}}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.05, duration: 0.6, ease }}
                    className={`flex aspect-square items-center justify-center rounded-md font-mono text-[7px] ${
                        [4, 10, 16].includes(i) ? "bg-ink text-cream ring-1 ring-gold" : "bg-sand text-ink/40"
                    }`}
                >
                    {[4, 10, 16].includes(i) ? <Check className="h-2.5 w-2.5 text-gold" strokeWidth={2.5} /> : i + 1}
                </motion.span>
            ))}
        </div>
    ),
    database: (
        <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1.5 rounded-xl border border-linen bg-ivory p-3">
                <Database className="h-4 w-4 text-ink/60" strokeWidth={1.5} />
                {[0.9, 0.6, 0.35].map((o, i) => (
                    <span key={i} className="h-1.5 rounded-full bg-ink/15" style={{ width: 44 - i * 12, opacity: o }} />
                ))}
            </div>
            <svg width="40" height="60" fill="none" className="shrink-0">
                <path d="M4 30 C 18 10, 22 50, 36 30" stroke="#D8A7A1" strokeWidth="1.4" className="line-flow" />
                <circle r="3" fill="#CBA368">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M4 30 C 18 10, 22 50, 36 30" />
                </circle>
            </svg>
            <div className="flex flex-col items-center gap-1 rounded-xl bg-ink px-3.5 py-3">
                <CalendarCheck className="h-4 w-4 text-gold" strokeWidth={1.5} />
                <span className="font-mono text-[7px] uppercase tracking-[0.16em] text-cream">Bookings</span>
            </div>
        </div>
    ),
    manual: (
        <div className="flex items-center gap-3">
            <div className="relative rounded-xl border border-linen bg-ivory p-3">
                <div className="grid grid-cols-3 gap-1">
                    {Array.from({ length: 9 }).map((_, i) => (
                        <span key={i} className="h-2.5 w-5 rounded-[3px] bg-ink/10" />
                    ))}
                </div>
                <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose font-serif text-sm text-white">✕</span>
                <div className="mt-2 text-center font-mono text-[7px] uppercase tracking-[0.16em] text-faint">Spreadsheets</div>
            </div>
            <svg width="36" height="20" fill="none" className="shrink-0">
                <path d="M2 10h28m0 0l-5-5m5 5l-5 5" stroke="#CBA368" strokeWidth="1.4" />
            </svg>
            <div className="flex flex-col items-center gap-1.5 rounded-xl border border-gold/40 bg-blush/50 px-3.5 py-3">
                <Workflow className="h-4 w-4 text-ink/70" strokeWidth={1.5} />
                <span className="font-mono text-[7px] uppercase tracking-[0.16em] text-ink/70">Automated</span>
            </div>
        </div>
    ),
    revenue: (
        <svg viewBox="0 0 220 90" fill="none" className="w-full">
            <line x1="10" y1="78" x2="210" y2="78" stroke="#E8E2D9" strokeWidth="1" />
            {[0, 1, 2].map((i) => (
                <line key={i} x1="10" y1={58 - i * 20} x2="210" y2={58 - i * 20} stroke="#E8E2D9" strokeWidth="0.6" strokeDasharray="3 4" />
            ))}
            <motion.path
                d="M12 72 C 60 66, 90 60, 120 44 S 180 22, 206 12"
                stroke="#CBA368"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.8, ease }}
            />
            <motion.circle
                cx="206" cy="12" r="4" fill="#D8A7A1"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.7, duration: 0.4 }}
            />
            <text x="150" y="86" className="fill-[#8C8A94]" fontSize="7" fontFamily="JetBrains Mono" letterSpacing="2">
                RECOVERED REVENUE ↑
            </text>
        </svg>
    ),
};

const ITEMS = [
    {
        v: "bookings",
        icon: CalendarCheck,
        title: "More repeat bookings",
        copy: "Turn more past clients back into active appointments.",
        testId: "benefit-repeat-bookings",
    },
    {
        v: "database",
        icon: Database,
        title: "Less reliance on new leads",
        copy: "Get more value from the database you already paid to build before increasing acquisition spend.",
        testId: "benefit-less-leads",
    },
    {
        v: "manual",
        icon: Workflow,
        title: "Less manual chasing",
        copy: "Your team doesn’t need to remember who is overdue and manually work through lists every week.",
        testId: "benefit-less-chasing",
    },
    {
        v: "revenue",
        icon: TrendingUp,
        title: "Measurable revenue",
        copy: "Know exactly which appointments came back through the reactivation system.",
        testId: "benefit-revenue",
    },
];

export const Benefits = () => (
    <section data-testid="benefits-section" className="bg-sand/60 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHead eyebrow="The outcome" title="What you actually get from it." align="center" />
            <div className="mt-12 grid gap-5 md:grid-cols-2">
                {ITEMS.map((b, i) => (
                    <Reveal key={b.v} delay={i * 0.08}>
                        <article data-testid={b.testId} className="card-lift grid h-full gap-6 rounded-2xl border border-linen bg-ivory p-6 sm:grid-cols-[1fr_auto] sm:items-center sm:p-7">
                            <div className="order-2 sm:order-1">
                                <div className="flex items-center gap-2.5">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blush">
                                        <b.icon className="h-3.5 w-3.5 text-ink" strokeWidth={1.5} />
                                    </span>
                                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold">
                                        Benefit {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="mt-3.5 font-serif text-xl uppercase tracking-tight text-ink sm:text-2xl">{b.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-mute">{b.copy}</p>
                            </div>
                            <div className="order-1 w-full sm:order-2 sm:w-[220px]">{V[b.v]}</div>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
